import React, { useState } from 'react'
import { TrashIcon, XMarkIcon } from '@heroicons/react/16/solid'
import { Button, ButtonText } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ENTITY_TYPES } from '@/data/constants/constants'
import useDeleteVaultGroup from '@/services/api/vault/useDeleteVaultGroup'
import useDeleteTransactionCategoryGroup from '@/services/api/transaction-category/useDeleteTransactionCategoryGroup'

type Props = {
  group: { id: number; name: string; entity_type: string }
  memberCount: number
}

/**
 * Row-menu action for vault and category groups (86cz4mkyb). An empty group
 * goes straight away; a group with members asks first, since its members move
 * back to the top level.
 */
export default function DeleteGroupButton({ group, memberCount }: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const isVaultGroup = group.entity_type === ENTITY_TYPES.VAULT_GROUP
  const { action: deleteVaultGroup, status: vaultStatus } =
    useDeleteVaultGroup()
  const { action: deleteCategoryGroup, status: categoryStatus } =
    useDeleteTransactionCategoryGroup()

  const pending =
    vaultStatus.status === 'pending' || categoryStatus.status === 'pending'
  const members = isVaultGroup ? 'vaults' : 'categories'

  async function deleteGroup() {
    if (isVaultGroup) await deleteVaultGroup({ id: group.id })
    else await deleteCategoryGroup({ id: group.id })
    setConfirmOpen(false)
  }

  return (
    <>
      <Button
        variant="destructiveGhost"
        className="w-full justify-start"
        disabled={pending}
        loading={pending}
        onClick={() => (memberCount > 0 ? setConfirmOpen(true) : deleteGroup())}
      >
        <TrashIcon />
        <ButtonText>Delete group</ButtonText>
      </Button>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete group - {group.name}</DialogTitle>
            <DialogClose>
              <XMarkIcon />
            </DialogClose>
          </DialogHeader>
          <DialogBody>
            <p>
              This group has {memberCount} {members}. Deleting the group keeps
              them and moves them out of the group. Nothing else changes.
            </p>
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              variant="destructive"
              loading={pending}
              disabled={pending}
              onClick={deleteGroup}
            >
              Delete group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
